import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./index.css";
import { RecipePopupProvider } from "./contexts/RecipePopupContext.jsx";
import { LanguageProvider } from "./contexts/LanguageContext.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.jsx";
import { useAuth } from "./hooks/useAuth.js";
import VisitorLayout from "./components/layout/VisitorLayout.jsx";
import UserLayout from "./components/layout/UserLayout";
import ProtectedRoute from "./components/auth/ProtectedRoute.jsx";
import VisitorHome from "./pages/visitor/VisitorHome.jsx";
import UserDashboard from "./pages/user/UserDashboard.jsx";
import AddRecipe from "./pages/user/AddRecipe.jsx";
import EditRecipeRedirect from "./pages/user/EditRecipeRedirect.jsx";
import FavoritesPage from "./pages/favorites/FavoritesPage.jsx";
import RecipesPage from "./pages/recipes/RecipesPage.jsx";
import ProfilePage from "./pages/profile/ProfilePage.jsx";
import RecipeDetailsPage from "./pages/RecipeDetailsPage.jsx";
import AuthPage from "./pages/AuthPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import WorldMenus from "./pages/WorldMenus";
import CommunityPage from "./pages/community/CommunityPage.jsx";
import FridgeAssistant from "./pages/user/FridgeAssistant.jsx";
import SharedRecipesPage from "./pages/shared/SharedRecipesPage.jsx";
import RecipePosterCreator from "./pages/user/RecipePosterCreator";
import UserManagement from "./pages/admin/UserManagement.jsx";
import AuthLanguageSync from "./components/common/AuthLanguageSync.jsx";
import AppLoadingScreen from "./components/common/AppLoadingScreen.jsx";

const isAdminUser = (currentUser) =>
  String(currentUser?.role || "").toLowerCase() === "admin" ||
  Boolean(currentUser?.is_admin || currentUser?.isAdmin);

/**
 * Composant principal de l'application
 * Interface moderne avec navbar et dashboard
 */
function App() {
  const { user, token, updateAuth, isLoading: authLoading } = useAuth();

  /**
   * Gérer les changements d'authentification
   */
  const handleAuthChange = (userData, userToken) => {
    console.log(
      "🔄 App.jsx - Changement d'authentification:",
      userData,
      userToken,
    );
    updateAuth(userData, userToken);
  };

  if (authLoading) {
    return (
      <AppLoadingScreen
        message="Chargement..."
        subMessage="Préparation de votre espace"
      />
    );
  }

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthLanguageSync />
        <RecipePopupProvider>
          <Router>
            <Routes>
              {/* ═══════════════════════════════════════════════════════════ */}
              {/* ROUTES PUBLIQUES — accessibles sans connexion              */}
              {/* ═══════════════════════════════════════════════════════════ */}

              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/"
                element={
                  <VisitorLayout>
                    <VisitorHome />
                  </VisitorLayout>
                }
              />
              <Route
                path="/recipe/:id"
                element={
                  user ? (
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <RecipeDetailsPage />
                    </UserLayout>
                  ) : (
                    <VisitorLayout>
                      <RecipeDetailsPage />
                    </VisitorLayout>
                  )
                }
              />

              {/* Pages publiques avec layout selon connexion (visiteur ou user) */}
              <Route
                path="/recipes"
                element={
                  user ? (
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <RecipesPage user={user} token={token} />
                    </UserLayout>
                  ) : (
                    <VisitorLayout>
                      <RecipesPage user={user} token={token} />
                    </VisitorLayout>
                  )
                }
              />
              <Route
                path="/world-menus"
                element={
                  user ? (
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <WorldMenus />
                    </UserLayout>
                  ) : (
                    <VisitorLayout>
                      <WorldMenus />
                    </VisitorLayout>
                  )
                }
              />
              <Route
                path="/community"
                element={
                  user ? (
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <CommunityPage user={user} token={token} />
                    </UserLayout>
                  ) : (
                    <VisitorLayout>
                      <CommunityPage user={user} token={token} />
                    </VisitorLayout>
                  )
                }
              />
              <Route
                path="/shared-recipes"
                element={
                  user ? (
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <SharedRecipesPage user={user} token={token} />
                    </UserLayout>
                  ) : (
                    <VisitorLayout>
                      <SharedRecipesPage user={user} token={token} />
                    </VisitorLayout>
                  )
                }
              />

              {/* ═══════════════════════════════════════════════════════════ */}
              {/* ROUTES PRIVÉES — authentification requise (ProtectedRoute)  */}
              {/* ═══════════════════════════════════════════════════════════ */}

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <UserDashboard user={user} token={token} />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <ProfilePage
                        user={user}
                        token={token}
                        onLogout={handleAuthChange}
                      />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/favorites"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <FavoritesPage user={user} token={token} />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fridge-assistant"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <FridgeAssistant />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-recipe"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <AddRecipe user={user} token={token} />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-poster"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <RecipePosterCreator user={user} token={token} />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-recipe/:id"
                element={
                  <ProtectedRoute user={user} token={token}>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <EditRecipeRedirect />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute user={user} token={token} requireAdmin>
                    <UserLayout user={user} onLogout={handleAuthChange}>
                      <UserManagement />
                    </UserLayout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </RecipePopupProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
